<template>
  <div class="container-fluid">
    <page-header
      :page="header.page"
      :actions="header.actions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <q-tab :active-tab="activeTab">
      <q-tab-item
        v-for="tab in tabs"
        :key="tab.value"
        :tabindex="tab.value"
        :title="tab.title"
        lazy
        @click="setTab(tab.value)"
      >
        <students-with-materials
          v-if="isStudentMaterialsEquipmentsAssignedTab"
        />
      </q-tab-item>
    </q-tab>
    <div v-if="isRequestsTab || isMaterialsEquipmentsTab">
      <filter-manager
        v-model="filter.model"
        :filters="filterOptions"
        class="filter-manager_extended"
        search-container-class="search-btn"
        @center="(val) => getMunicipalities(val?.id)"
        @municipality="(val) => getSchools(val?.id)"
        @search="search()"
      />
      <custom-separator :text="separatorTitle" class="my-4" />

      <div v-if="hasResults" class="search-result">
        <template v-if="isRequestsTab">
          <result-row
            v-for="(item, index) in results"
            :key="index"
            :item="item"
            :actions-to-show="resultRowActions"
            @open="handlerOpen"
            @close="handlerClose"
            @refresh="() => search(pagination.currentPage)"
          />
        </template>
        <template v-else-if="isMaterialsEquipmentsTab">
          <material-result-row
            v-for="(item, index) in results"
            :key="index"
            :item="item"
            :actions-to-show="materialResultRowActions"
            @open="handlerOpen"
            @close="handlerClose"
            @refresh="() => search(pagination.currentPage)"
          />
        </template>
        <pagination
          v-if="
            pagination.totalItemCount > pagination.pageSize &&
              (isRequestsTab || isMaterialsEquipmentsTab)
          "
          :data="pagination"
          :page-size="pagination.pageSize"
          @pagination-go-page="search"
        />
      </div>

      <custom-result-not-found v-else :text="notResultsFoundTitle" />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import FilterManager from 'FilterManager';
import { QTab, QTabItem } from '@/components/common/q-tabs';
import ResultRow from './components/ResultRow.vue';
import storeMixin from '@/mixin/storeGenerator';
import MaterialResultRow from './components/MaterialResultRow.vue';
import locationService from '@/services/api/location.api';

// services
import materialsAndEquipmentsService from '@/services/api/materialsAndEquipments.api';
import inventoryOrderMaterialsAndEquipments from '@/services/api/inventoryOrderMaterialsAndEquipments.api';
import inventoryCatalogService from '@/services/api/inventorycatalog.api';
import * as bookService from '@/modules/books-requests/api';
import Pagination from 'pagination';
import ACTIONS from '@/utils/constants/inventoryOrderMaterialEquipmentsRowAction.js';
import BOOK_ROW_ACTIONS from '@/utils/constants/inventoryBookRowActions';
import StudentsWithMaterials from './tabs/StudentsWithMaterials.vue';

const MANAGE_ROUTE_NAME = 'materials-and-equipments-requests-add';
const TABS = Object.freeze({
  Requests: 0,
  MaterialsEquipments: 1,
  StudentMaterialsEquipmentsAssigned: 2,
});

const DEFAULT_PAGE_SIZE = 10;

export default {
  name: 'MaterialsAndEquipmentsRequests',
  mixins: [storeMixin],
  components: {
    PageHeader,
    CustomSeparator,
    CustomResultNotFound,
    FilterManager,
    QTab,
    QTabItem,
    Pagination,
    ResultRow,
    StudentsWithMaterials,
    MaterialResultRow,
  },
  data() {
    return {
      results: [],
      activeTab: TABS.Requests,
      filter: {
        model: {
          catalog: null,
          category: null,
          saloonType: null,
          status: null,
          center: null,
          municipality: null,
          school: null,
          requestCode: null,
          teacher: null,
        },
        options: {
          catalogs: [],
          categories: [],
          saloonTypes: [],
          statuses: [],
          centers: [],
          municipalities: [],
          schools: [],
        },
      },
      pagination: {
        count: 0,
        currentPage: 1,
        pageCount: 1,
        totalItemCount: 0,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      results: [],
    };
  },
  async mounted() {
    await Promise.allSettled([
      this.getCatalogs(),
      this.getCategories(),
      this.getSaloonTypes(),
      this.getStatuses(),
      this.getCenters(),
    ]);
  },
  created() {
    this.activeTab = this.tabParam;
  },
  computed: {
    tabs() {
      return Object.entries(TABS).map(([name, value]) => {
        const obj = { name, value };
        switch (value) {
          case TABS.Requests:
            obj.title = 'Solicitudes';
            break;
          case TABS.MaterialsEquipments:
            obj.title = 'Materiales/Equipos';
            break;
          case TABS.StudentMaterialsEquipmentsAssigned:
            obj.title = 'Estudiantes con Materiales/Equipos Asignados';
            break;
          default:
            break;
        }
        return obj;
      });
    },
    tabParam() {
      return Number(this.$route.query.tab ?? TABS.Requests);
    },
    isRequestsTab() {
      return this.activeTab === TABS.Requests;
    },
    isMaterialsEquipmentsTab() {
      return this.activeTab === TABS.MaterialsEquipments;
    },
    isStudentMaterialsEquipmentsAssignedTab() {
      return this.activeTab === TABS.StudentMaterialsEquipmentsAssigned;
    },
    academicYearId() {
      return this.currentAcademicYear().id;
    },
    header() {
      return {
        page: {
          icon: 'fas fa-book-open',
          title: 'Solicitudes de Material/Equipo',
          routes: ['Inicio', 'PEI', 'Solicitudes de Material/Equipo'],
        },
        actions: [
          {
            text: 'Regresar',
            icon: 'fas fa-arrow-circle-left',
            variant: 'outline-success',
            click: () => this.$router.back(),
          },
          {
            text: 'Crear Solicitud',
            icon: 'far fa-plus-circle',
            variant: 'success',
            click: () => this.add(),
          },
        ],
      };
    },
    filterOptions() {
      return [
        {
          name: 'catalog',
          component: 'CustomDropDown',
          options: this.filter.options.catalogs,
          value: this.filter.model.catalog,
          placeholder: 'Título',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'category',
          component: 'CustomDropDown',
          options: this.filter.options.categories,
          value: this.filter.model.category,
          placeholder: 'Categoría',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'saloonType',
          component: 'CustomDropDown',
          options: this.filter.options.saloonTypes,
          value: this.filter.model.saloonType,
          placeholder: 'Tipo de Salón',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          value: this.filter.model.center,
          options: this.filter.options.centers,
          placeholder: 'Centro',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          value: this.filter.model.municipality,
          options: this.filter.options.municipalities,
          placeholder: 'Municipio',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          disabled: !this.filter.model.center,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          value: this.filter.model.school,
          options: this.filter.options.schools,
          placeholder: 'Escuela',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          disabled: !this.filter.model.municipality,
        },
        {
          name: 'requestCode',
          component: 'CustomInput',
          value: this.filter.model.requestCode,
          placeholder: 'Número de Solicitud',
          icon: 'fa fa-search orange-icon',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          type: 'text',
          leftIcon: true,
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'teacher',
          component: 'CustomInput',
          value: this.filter.model.teacher,
          placeholder: 'Nombre del Maestro',
          icon: 'fa fa-search orange-icon',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          type: 'text',
          leftIcon: true,
          clearable: true,
          isAdvanced: true,
        },
      ];
    },
    hasResults() {
      return Boolean(this.results.length);
    },
    separatorTitle() {
      const prefix = 'Resultados de';
      const article = this.activeTab === TABS.Requests ? 'las' : 'los';
      const { title } = this.tabs.find((x) => x.value === this.activeTab);
      return `${prefix} ${article} ${title}`;
    },
    notResultsFoundTitle() {
      const prefix = 'No se han encontrado resultados de';
      const article = this.activeTab === TABS.Requests ? 'las' : 'los';
      const { title } = this.tabs.find((x) => x.value === this.activeTab);
      return `${prefix} ${article} ${title.toLowerCase()}.`;
    },
    resultRowActions() {
      return [
        ACTIONS.Info,
        ACTIONS.History,
        ACTIONS.ReceivedByMaster,
        ACTIONS.PurchaseInfo,
        ACTIONS.ViewAssignedBook,
        ACTIONS.ContinueBookRequest,
        ACTIONS.Documents,
      ];
    },
    materialResultRowActions() {
      return [
        BOOK_ROW_ACTIONS.AssignStudent,
        BOOK_ROW_ACTIONS.UnassignStudent,
        BOOK_ROW_ACTIONS.CustodianHistory,
        BOOK_ROW_ACTIONS.Seize,
        BOOK_ROW_ACTIONS.AdditionalInfo,
        BOOK_ROW_ACTIONS.TransferInfo,
      ];
    },
  },
  methods: {
    add() {
      this.$router.push({ name: MANAGE_ROUTE_NAME });
    },
    async search(currentPage = 1) {
      const {
        catalog,
        category,
        saloonType,
        status,
        center,
        municipality,
        school,
        requestCode,
        teacher,
        //studentNameOrsie,
      } = this.filter.model;

      const { pageSize } = this.pagination;

      const search = this.isRequestsTab
        ? inventoryOrderMaterialsAndEquipments.search
        : inventoryOrderMaterialsAndEquipments.searchMyMaterials;

      const { data } = await search({
        catalogId: catalog?.id,
        categoryId: category?.id,
        saloonTypeId: saloonType?.id,
        statusId: status?.id,
        centerId: center?.id,
        municipalityId: municipality?.id,
        schoolId: school?.id,
        requestCode,
        teacher,
        //studentNameOrsie,
        pageIndex: currentPage,
        pageSize,
      });

      this.results = data.data.map((x) => ({
        ...x,
        isOpen: false,
        selected: false,
      }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async getCatalogs() {
      const { data } = await inventoryCatalogService.getKeyValue({ type: 2 });
      this.filter.options.catalogs = data;
    },
    async getCategories() {
      const { data } = await materialsAndEquipmentsService.getCategories();
      this.filter.options.categories = data;
    },
    async getSaloonTypes() {
      const { data } = await materialsAndEquipmentsService.getSaloon();
      this.filter.options.saloonTypes = data;
    },
    async getStatuses() {
      const fn = this.isRequestsTab
        ? bookService.getStatusList
        : materialsAndEquipmentsService.getInventoryStatus;
      const { data } = await fn();
      this.filter.options.statuses = data;
    },
    async getCenters() {
      const { data } = await locationService.getCenters({
        academicYearId: this.academicYearId,
      });
      this.filter.options.centers = data;

      if (data.length === 1) {
        this.filter.model.center = data[0];
        this.getMunicipalities(data[0].id);
      }
    },
    async getMunicipalities(centerId) {
      this.filter.model.municipality = null;
      this.filter.model.school = null;
      this.filter.options.municipalities = [];
      this.filter.options.schools = [];

      if (!centerId) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId,
      });
      this.filter.options.municipalities = data;

      if (data.length === 1) {
        this.filter.model.municipality = data[0];
        this.getSchools(data[0].id);
      }
    },
    async getSchools(municipalityId) {
      this.filter.model.school = null;
      this.filter.options.schools = [];

      if (!municipalityId) return;

      const academicYearId = this.currentAcademicYear().id;
      const { data } = await locationService.getSchools({
        academicYearId,
        centerId: this.filter.model.center.id,
        municipalityId,
      });
      this.filter.options.schools = data;
      if (data.length === 1) this.filter.model.school = data[0];
    },
    async setTab(tab) {
      const sameRoute = this.activeTab === tab;
      if (sameRoute) return;

      this.activeTab = tab;
      this.results = [];
      this.getStatuses();

      if (tab) this.$router.replace({ query: { tab } });

      const tabsToReload = [TABS.Requests, TABS.MaterialsEquipments];

      if (tabsToReload.includes(tab)) {
        await this.search();
      }
    },
    async handlerOpen(item) {
      this.results
        .filter((x) => x.id !== item.id && x.isOpen)
        .forEach((x) => (x.isOpen = false));

      item.isOpen = true;
    },
    handlerClose(item) {
      item.isOpen = false;
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const routes = [MANAGE_ROUTE_NAME];
    next(async (vm) => {
      if (routes.includes(from.name)) {
        vm.recoveryData();
        await vm.search();
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
};
</script>

<style scoped>
::v-deep .custom-datetime-picker-container.width-container {
  width: 100% !important;
}
</style>
